import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertCircle,
  CheckCircle2,
  Copy,
  Globe,
  Plus,
  RefreshCw,
  Save,
  Trash2,
  X,
} from 'lucide-react';
import { api } from '../../api/client';
import EmptyState from '../../components/EmptyState';
import { SkeletonTable } from '../../components/Skeleton';
import { toast } from '../../lib/toast';

// Admin → Subdomains (Phase 4 Slice J).
//
// Manage whitelabel hosts (track.<brand> and reports.<brand>) for resellers.
// Super-admin sees every reseller's rows + can pick which reseller to add for.
// Reseller-owners see only their own rows + can only add for themselves.
//
// Verification = DNS TXT record at `_rankonai-verify.<hostname>` containing
// the row's `verificationToken`. Customer adds the TXT, hits Verify here,
// backend resolves the TXT, sets verifiedAt.
export default function SubdomainsPage() {
  const qc = useQueryClient();
  const [creating, setCreating] = useState(false);
  const [draft, setDraft] = useState({ reseller_id: '', type: 'tracking', hostname: '' });

  const subdomainsQuery = useQuery({
    queryKey: ['admin-subdomains'],
    queryFn: () => api.get('/api/admin/brand-subdomains').then((r) => r.data),
  });

  const scope = subdomainsQuery.data?.scope;
  const isReseller = scope?.kind === 'reseller';
  const rows = subdomainsQuery.data?.subdomains || [];

  // Reseller picker — admin only.
  const resellersQuery = useQuery({
    queryKey: ['admin-resellers-pick'],
    queryFn: () => api.get('/api/admin/resellers').then((r) => r.data.resellers),
    enabled: scope?.kind === 'admin',
    retry: false,
  });

  const createMutation = useMutation({
    mutationFn: (body) =>
      api.post('/api/admin/brand-subdomains', body).then((r) => r.data.subdomain),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-subdomains'] });
      setCreating(false);
      setDraft({ reseller_id: '', type: 'tracking', hostname: '' });
      toast.success('Subdomain added — verify DNS to activate');
    },
    onError: (err) => toast.error(err, 'Could not add subdomain'),
  });

  const verifyMutation = useMutation({
    mutationFn: (id) =>
      api.post(`/api/admin/brand-subdomains/${id}/verify`).then((r) => r.data),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ['admin-subdomains'] });
      if (data.verifyResult?.ok) {
        toast.success(`Verified ${data.subdomain.hostname}`);
      } else {
        toast.error(
          new Error(data.verifyResult?.error || 'unknown'),
          'DNS not ready yet',
        );
      }
    },
    onError: (err) => toast.error(err, 'Verify request failed'),
  });

  const toggleMutation = useMutation({
    mutationFn: ({ id, isActive }) =>
      api.put(`/api/admin/brand-subdomains/${id}`, { isActive }).then((r) => r.data.subdomain),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-subdomains'] }),
    onError: (err) => toast.error(err, 'Could not update'),
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => api.delete(`/api/admin/brand-subdomains/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-subdomains'] });
      toast.success('Subdomain removed');
    },
    onError: (err) => toast.error(err, 'Could not delete'),
  });

  function commitCreate() {
    if (!draft.hostname || !draft.type) {
      toast.error('Hostname and type are required');
      return;
    }
    if (scope?.kind === 'admin' && !draft.reseller_id) {
      toast.error('Pick a reseller');
      return;
    }
    const body = { type: draft.type, hostname: draft.hostname.toLowerCase().trim() };
    if (scope?.kind === 'admin') body.reseller_id = draft.reseller_id;
    createMutation.mutate(body);
  }

  return (
    <div className="card">
      <div className="section-header flex-wrap gap-3">
        <div className="section-title">
          <span className="section-icon">
            <Globe className="w-4 h-4" />
          </span>
          Brand subdomains
          {subdomainsQuery.data && (
            <span className="text-xs text-ink-400 font-normal ml-1">({rows.length})</span>
          )}
        </div>
        {!creating && (
          <button type="button" className="btn-primary text-xs" onClick={() => setCreating(true)}>
            <Plus className="w-3 h-3" /> Add subdomain
          </button>
        )}
      </div>

      {isReseller && (
        <div className="rounded-xl border border-brand-100 bg-brand-50/60 text-brand-800 text-sm p-3 mb-3 flex items-start gap-2">
          <Globe className="w-4 h-4 flex-none mt-0.5" />
          <div>
            <div className="font-semibold">Your whitelabel hosts ({scope.brandName})</div>
            <div className="text-xs mt-1">
              Add one tracking host (e.g. track.yourbrand.com) and one reports host
              (e.g. reports.yourbrand.com). After CNAME + TXT setup, hit Verify.
            </div>
          </div>
        </div>
      )}

      <p className="text-sm text-ink-500 mb-3">
        Whitelabel host setup is two-step: (1) CNAME the hostname at our edge so
        requests route here, and (2) add a TXT record at{' '}
        <code className="text-[10px]">_rankonai-verify.&lt;hostname&gt;</code> containing
        the row's verification token. The Verify button checks the TXT record live.
      </p>

      {creating && (
        <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 mb-4 space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {scope?.kind === 'admin' && (
              <label className="text-xs text-ink-600">
                Reseller
                <select
                  className="input mt-1"
                  value={draft.reseller_id}
                  onChange={(e) => setDraft({ ...draft, reseller_id: e.target.value })}
                >
                  <option value="">— pick one —</option>
                  {(resellersQuery.data || []).map((r) => (
                    <option key={r._id} value={r._id}>
                      {r.brandName}
                    </option>
                  ))}
                </select>
              </label>
            )}
            <label className="text-xs text-ink-600">
              Type
              <select
                className="input mt-1"
                value={draft.type}
                onChange={(e) => setDraft({ ...draft, type: e.target.value })}
              >
                <option value="tracking">tracking</option>
                <option value="reports">reports</option>
              </select>
            </label>
            <label className="text-xs text-ink-600">
              Hostname
              <input
                className="input mt-1 font-mono text-xs"
                placeholder="e.g. track.yourbrand.com"
                value={draft.hostname}
                onChange={(e) => setDraft({ ...draft, hostname: e.target.value })}
                maxLength={253}
              />
            </label>
          </div>
          <div className="flex justify-end gap-2">
            <button
              type="button"
              className="btn-ghost text-xs"
              onClick={() => {
                setCreating(false);
                setDraft({ reseller_id: '', type: 'tracking', hostname: '' });
              }}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn-primary text-xs"
              disabled={createMutation.isPending}
              onClick={commitCreate}
            >
              <Save className="w-3 h-3" /> Add
            </button>
          </div>
        </div>
      )}

      {subdomainsQuery.isLoading ? (
        <SkeletonTable rows={3} cols={5} wrap={false} />
      ) : rows.length === 0 && !creating ? (
        <EmptyState
          icon={Globe}
          title="No brand subdomains yet"
          body="Add a tracking or reports host once your customer has CNAME'd their hostname at our edge."
        />
      ) : (
        <div className="space-y-3">
          {rows.map((row) => (
            <SubdomainCard
              key={row._id}
              row={row}
              isAdmin={scope?.kind === 'admin'}
              verifyPending={verifyMutation.isPending && verifyMutation.variables === row._id}
              onVerify={() => verifyMutation.mutate(row._id)}
              onToggle={() =>
                toggleMutation.mutate({ id: row._id, isActive: !row.isActive })
              }
              onDelete={() => {
                if (
                  window.confirm(
                    `Remove ${row.hostname}? Requests to it will stop being branded immediately.`,
                  )
                ) {
                  deleteMutation.mutate(row._id);
                }
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function SubdomainCard({ row, isAdmin, verifyPending, onVerify, onToggle, onDelete }) {
  const verified = !!row.verifiedAt;
  return (
    <div className="rounded-lg border border-gray-200 p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-medium text-ink-800">{row.hostname}</span>
            <TypePill type={row.type} />
            {verified ? (
              <span className="pill bg-emerald-50 text-emerald-700">
                <CheckCircle2 className="w-3 h-3" /> Verified
              </span>
            ) : (
              <span className="pill bg-amber-50 text-amber-700">
                <AlertCircle className="w-3 h-3" /> Awaiting TXT
              </span>
            )}
            {!row.isActive && (
              <span className="pill bg-gray-100 text-gray-500">Paused</span>
            )}
          </div>
          {isAdmin && row.reseller_id?.brandName && (
            <div className="text-xs text-ink-400 mt-1">
              Reseller: {row.reseller_id.brandName}
            </div>
          )}
          {row.lastVerifyError && !verified && (
            <div className="text-xs text-rose-600 mt-1">
              Last error: <code>{row.lastVerifyError}</code>
            </div>
          )}
        </div>
        <div className="flex items-center gap-1 flex-none">
          <button
            type="button"
            className="btn-ghost text-xs"
            onClick={onVerify}
            disabled={verifyPending}
            title="Re-run the DNS TXT check"
          >
            <RefreshCw className={`w-3 h-3 ${verifyPending ? 'animate-spin' : ''}`} />
            {verifyPending ? 'Checking…' : 'Verify'}
          </button>
          <button type="button" className="btn-ghost text-xs" onClick={onToggle}>
            {row.isActive ? 'Pause' : 'Resume'}
          </button>
          <button
            type="button"
            className="btn-ghost text-xs text-rose-600 hover:text-rose-700"
            onClick={onDelete}
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      {!verified && (
        <div className="mt-3 rounded-md bg-gray-50 border border-gray-200 p-3 text-xs space-y-2">
          <div className="font-semibold text-ink-700">DNS setup</div>
          <div>
            <div className="text-ink-500 mb-1">1. CNAME (at your DNS provider)</div>
            <CopyableRow
              label="Host"
              value={row.hostname}
            />
            <CopyableRow label="Target" value={extractEdgeTarget()} />
          </div>
          <div>
            <div className="text-ink-500 mb-1 mt-2">2. TXT record (verification)</div>
            <CopyableRow label="Host" value={row.verifyHostname} />
            <CopyableRow label="Value" value={row.verificationToken} />
          </div>
        </div>
      )}
    </div>
  );
}

function TypePill({ type }) {
  if (type === 'tracking') {
    return (
      <span className="pill bg-cyan-50 text-cyan-700 font-mono text-[10px]">tracking</span>
    );
  }
  return (
    <span className="pill bg-violet-50 text-violet-700 font-mono text-[10px]">reports</span>
  );
}

function CopyableRow({ label, value }) {
  return (
    <div className="flex items-center gap-2">
      <span className="text-[10px] text-ink-400 w-12 flex-none uppercase">{label}</span>
      <code className="text-[10px] font-mono text-ink-700 bg-white border border-gray-200 rounded px-2 py-1 break-all flex-1">
        {value}
      </code>
      <button
        type="button"
        className="btn-ghost text-[10px] flex-none"
        onClick={() => {
          navigator.clipboard.writeText(value);
          toast.success('Copied');
        }}
      >
        <Copy className="w-3 h-3" />
      </button>
    </div>
  );
}

function extractEdgeTarget() {
  if (typeof window === 'undefined') return 'rankonai.com';
  try {
    return new URL(window.location.origin).hostname;
  } catch {
    return 'rankonai.com';
  }
}
